/**
 * Доска плинко: колышки, корзины и котик, который падает по уже известному пути.
 *
 * Путь приходит с сервера. Клиент только анимирует шаги — так картинка не может
 * «додумать» другую корзину, чем та, за которую уже списали и начислили лапки.
 *
 * Геометрия — классическая доска Гальтона: в ряду r стоит r+1 колышек, и котик
 * попадает точно по колышку на каждом ряду. Позиция считается не индексами в
 * массиве колышков, а смещением от центра в половинах ширины корзины: после
 * одиннадцати шагов смещение равно 2·bucket − 11, поэтому котик приземляется
 * ровно в центр той корзины, которую посчитал сервер.
 */

import { useEffect, useRef } from "react";

import { PLINKO_MULTIPLIERS, PLINKO_ROWS, formatCompactMultiplier } from "@koshkodrop/shared";

import { drawCat } from "../crash/cat";

interface Props {
  path: number[] | null;
  bucket: number | null;
  running: boolean;
  onDone: () => void;
}

/** Время на один ряд. Кривая разгона добавляет ускорение к низу доски. */
const ROW_MS = 165;
/** Сколько котик устраивается в корзине после последнего колышка. */
const SETTLE_MS = 520;
/** Высота отскока от колышка. Маленькая: падение должно читаться плавным. */
const HOP_PX = 3.5;
/** Длина следа за котиком в кадрах. */
const TRAIL = 14;

export function PlinkoBoard({ path, bucket, running, onDone }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Колбэк держим в ref: иначе новая функция на каждом рендере родителя
  // перезапускала бы эффект, и падение начиналось бы заново с верха доски
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const layout = layoutBoard(width, height);
    const started = performance.now();
    const trail: { x: number; y: number }[] = [];
    let frame = 0;
    let finished = false;

    const render = (time: number) => {
      ctx.clearRect(0, 0, width, height);

      if (path && running) {
        const state = catState(layout, path, time - started);
        drawBoard(ctx, layout, bucket, time, state.depth);

        trail.push({ x: state.x, y: state.y });
        if (trail.length > TRAIL) trail.shift();
        drawTrail(ctx, trail);

        drawSquashedCat(ctx, state);

        if (state.done && !finished) {
          finished = true;
          onDoneRef.current();
        }
      } else {
        drawBoard(ctx, layout, bucket, time, null);
        drawCat(ctx, {
          x: layout.centerX,
          y: layout.startY,
          size: 34,
          pose: "sit",
          time,
        });
      }

      frame = requestAnimationFrame(render);
    };

    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [path, bucket, running]);

  const activeBucket = running ? null : bucket;

  return (
    <div className="overflow-hidden rounded-3xl border border-white/5 bg-night-900">
      <canvas ref={canvasRef} className="block h-[392px] w-full" />
      <div
        className="grid gap-0.5 px-3.5 pb-2"
        style={{ gridTemplateColumns: `repeat(${PLINKO_MULTIPLIERS.length}, minmax(0, 1fr))` }}
      >
        {PLINKO_MULTIPLIERS.map((multiplier, index) => {
          const hot = activeBucket === index;
          const edge = index === 0 || index === PLINKO_MULTIPLIERS.length - 1;
          return (
            <div
              key={index}
              className={`rounded-md px-0.5 py-1.5 text-center ${
                hot
                  ? "bg-paws-500/25 text-paws-300"
                  : edge
                    ? "bg-win-500/10 text-win-400"
                    : "bg-white/[0.04] text-white/55"
              }`}
            >
              <span className="block text-[10px] font-extrabold leading-none">
                {formatCompactMultiplier(multiplier)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type Layout = ReturnType<typeof layoutBoard>;

function layoutBoard(width: number, height: number) {
  const rows = PLINKO_ROWS;
  const buckets = PLINKO_MULTIPLIERS.length;

  const pegTop = 40;
  const landY = height - 26;
  // Шаг по вертикали одинаков для всех рядов и для последнего пролёта в корзину
  const rowGap = (landY - pegTop) / rows;

  const usable = width - 28;
  const bucketWidth = usable / buckets;

  return {
    rows,
    buckets,
    bucketWidth,
    halfStep: bucketWidth / 2,
    bucketLeft: (width - usable) / 2,
    centerX: width / 2,
    pegTop,
    rowGap,
    landY,
    startY: pegTop - 30,
    bucketY: landY + 8,
    width,
    height,
  };
}

/** Центр колышка по ряду и смещению в половинах ширины корзины. */
function pegAt(layout: Layout, row: number, offset: number): { x: number; y: number } {
  return {
    x: layout.centerX + offset * layout.halfStep,
    y: layout.pegTop + row * layout.rowGap,
  };
}

function drawBoard(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  highlight: number | null,
  time: number,
  depth: number | null,
): void {
  ctx.fillStyle = "#0b0a1f";
  ctx.fillRect(0, 0, layout.width, layout.height);

  // Ряд r содержит r+1 колышек: смещения −r, −r+2, … , r
  for (let row = 0; row < layout.rows; row += 1) {
    // Колышек, который котик проходит прямо сейчас, вспыхивает
    const touched = depth === null ? -1 : Math.floor(depth);
    const glow = touched === row ? 1 - Math.min(Math.max(depth! - row, 0), 1) : 0;

    for (let i = 0; i <= row; i += 1) {
      const peg = pegAt(layout, row, -row + i * 2);

      ctx.beginPath();
      ctx.arc(peg.x, peg.y, 3.2 + glow * 1.6, 0, Math.PI * 2);
      ctx.fillStyle = "#ffc65c";
      ctx.globalAlpha = 0.8 + glow * 0.2;
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  for (let i = 0; i < layout.buckets; i += 1) {
    const x = layout.bucketLeft + i * layout.bucketWidth;
    const hot = highlight === i;
    ctx.fillStyle = hot ? "rgba(245, 166, 35, 0.18)" : "rgba(255,255,255,0.03)";
    ctx.fillRect(x + 1, layout.bucketY - 14, layout.bucketWidth - 2, 18);
    if (hot) {
      ctx.globalAlpha = 0.35 + Math.sin(time / 120) * 0.1;
      ctx.fillStyle = "#ffc65c";
      ctx.fillRect(x + 1, layout.bucketY - 14, layout.bucketWidth - 2, 18);
      ctx.globalAlpha = 1;
    }
  }
}

function drawTrail(ctx: CanvasRenderingContext2D, trail: { x: number; y: number }[]): void {
  for (let i = 0; i < trail.length; i += 1) {
    const point = trail[i]!;
    const fade = (i + 1) / trail.length;

    ctx.beginPath();
    ctx.arc(point.x, point.y, 2 + fade * 3, 0, Math.PI * 2);
    ctx.fillStyle = "#ffc65c";
    ctx.globalAlpha = fade * 0.16;
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

interface CatState {
  x: number;
  y: number;
  tilt: number;
  /** Сжатие по вертикали при ударе о дно: 1 — обычная форма. */
  squash: number;
  /** Глубина в рядах: нужна доске, чтобы подсветить пройденный колышек. */
  depth: number;
  landed: boolean;
  done: boolean;
}

function drawSquashedCat(ctx: CanvasRenderingContext2D, state: CatState): void {
  ctx.save();
  ctx.translate(state.x, state.y);
  // Сжатие делается снаружи: drawCat не знает про деформацию, а масштаб по
  // одной оси даёт нужный «шлепок» без правок фигуры котика
  ctx.scale(1, state.squash);
  drawCat(ctx, {
    x: 0,
    y: 0,
    size: 34,
    pose: state.landed ? "cheer" : "run",
    time: performance.now(),
    rotation: state.tilt,
  });
  ctx.restore();
}

/**
 * Положение котика в момент elapsedMs.
 *
 * Вертикаль идёт с лёгким ускорением, горизонталь между колышками сглажена
 * функцией со нулевой скоростью на краях — поэтому на границе ряда движение не
 * рвётся, и падение читается плавным, а не пошаговым.
 */
function catState(layout: Layout, path: number[], elapsedMs: number): CatState {
  const rows = path.length;
  const fallMs = rows * ROW_MS;

  if (elapsedMs >= fallMs) {
    return landing(layout, path, elapsedMs - fallMs);
  }

  const t = elapsedMs / fallMs;
  // Разгон к низу доски: на старте медленнее, у корзин быстрее
  const eased = t * (0.72 + 0.28 * t);
  // Котик начинает выше первого колышка, поэтому глубина стартует с отрицательной
  const depth = -0.9 + (rows + 0.9) * eased;

  const row = Math.min(Math.max(Math.floor(depth), 0), rows - 1);
  const frac = depth <= 0 ? 0 : Math.min(depth - row, 1);
  const smooth = frac * frac * (3 - 2 * frac);

  let rights = 0;
  for (let i = 0; i < row; i += 1) rights += path[i]!;

  const step = path[row]!;
  const from = 2 * rights - row;
  const offset = depth <= 0 ? 0 : from + (2 * step - 1) * smooth;

  // Отскок от колышка: гаснет к низу, где котик уже разогнался
  const hop = HOP_PX * Math.sin(Math.PI * frac) * (1 - 0.5 * t);
  // Наклон пропорционален горизонтальной скорости, поэтому на колышке он ноль
  const speed = (6 * frac * (1 - frac)) / 1.5;

  return {
    x: layout.centerX + offset * layout.halfStep,
    y: layout.pegTop + depth * layout.rowGap - hop,
    tilt: (step === 1 ? 1 : -1) * 0.26 * speed,
    squash: 1,
    depth,
    landed: false,
    done: false,
  };
}

/** Приземление: два затухающих касания дна корзины и остановка. */
function landing(layout: Layout, path: number[], sinceLandingMs: number): CatState {
  const bucket = path.reduce((sum, step) => sum + step, 0);
  const progress = Math.min(sinceLandingMs / SETTLE_MS, 1);
  const decay = 1 - progress;
  const wave = Math.cos(progress * Math.PI * 2.4);

  return {
    x: layout.bucketLeft + (bucket + 0.5) * layout.bucketWidth,
    y: layout.landY - Math.max(wave, 0) * decay * 13,
    tilt: 0,
    // Сжимается в момент касания, когда волна внизу
    squash: 1 - Math.max(-wave, 0) * decay * 0.22,
    depth: path.length,
    landed: true,
    done: progress >= 1,
  };
}
